import {Injectable} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {catchError, filter, mergeMap, tap, withLatestFrom} from 'rxjs/operators';
import {defer, of} from 'rxjs';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {
    AuthActionTypes,
    CurrentUserLoadedAction,
    LoadCurrentUserAction,
    LoginAction,
    LogoutAction,
    RegisterUserAction, ReloadCurrentUserAction
} from '../actions/auth.actions';
import {AuthService} from '../services';
import {AppState} from '../reducers';
import {environment} from '../../../environments/environment';
import {isUserLoaded} from '../selectors/auth.selectors';
import {
    UserModel
} from '../models/user.model';
import {TranslateService} from '@ngx-translate/core';
import {parse} from 'querystring';
import {ClearAllLatestChangesAction} from '../actions/all_latest_changes.actions';
import {ClearProjectsAction} from '../actions/project.actions';
import {ClearDashboardAction} from '../actions/dashboard.actions';
import { UpdateDirectChatUnreadIdsAction } from '../actions/is_read.actions';
import { MobileAppService } from '../services/mobile-app.service';

const MOBILE_APP_TOKEN_DELETION_URL_AFTER_LOGOUT = `${environment.pathToAPI}/logout-from-mobile-and-disable-push-notification`;
const REGISTER_DEVICE_TOKEN_URL = `${environment.pathToAPI}/reg-device-token-to-sns`;

@Injectable()
export class AuthEffects {

    login$ = createEffect(() => this.actions$.pipe(
        ofType<LoginAction>(AuthActionTypes.LOGIN),
        tap(action => {
            localStorage.setItem(environment.authTokenKey, action.payload.authToken);
            this.store.dispatch(new LoadCurrentUserAction({token: action.payload.pathToken}));
        }),
    ), {dispatch: false});

    logout$ = createEffect(() => this.actions$.pipe(
        ofType<LogoutAction>(AuthActionTypes.LOGOUT),
        tap(() => {
            localStorage.removeItem(environment.authTokenKey);
            localStorage.removeItem(environment.salesforceKey);
            this.store.dispatch(new ClearAllLatestChangesAction());
            this.store.dispatch(new ClearProjectsAction());
            this.store.dispatch(new ClearDashboardAction());
            this.router.navigate(['/auth/login'], {queryParams: {returnUrl: this.returnUrl}});
        })
    ), {dispatch: false});

    logoutSuumo$ = createEffect(() => this.actions$.pipe(
        ofType<LogoutAction>(AuthActionTypes.LOGOUT_SUUMO),
        tap(() => {
            localStorage.removeItem(environment.authTokenKey);
            localStorage.removeItem(environment.salesforceKey);
            this.store.dispatch(new ClearAllLatestChangesAction());
            this.store.dispatch(new ClearProjectsAction());
            this.store.dispatch(new ClearDashboardAction());
            this.router.navigate(['/auth/suumo-login'], {queryParams: {returnUrl: this.returnUrl}});
        })
    ), {dispatch: false});

    register$ = createEffect(() => this.actions$.pipe(
        ofType<RegisterUserAction>(AuthActionTypes.REGISTER_USER),
        tap(action => {
            localStorage.setItem(environment.authTokenKey, action.payload.authToken);
        })
    ), {dispatch: false});

    currentUserRequested$ = createEffect(() => this.actions$.pipe(
        ofType<LoadCurrentUserAction>(AuthActionTypes.LOAD_CURRENT_USER),
        withLatestFrom(this.store.pipe(select(isUserLoaded))),
        filter(([action, loaded]) => !loaded),
        mergeMap(([action, loaded]) => this.authService.getUserByToken(action.payload.token)),
        catchError((error, caught) => {
            this.store.dispatch(new LogoutAction());
            return caught;
        }),
        tap((user: UserModel) => {
            if (user) {
                this.authService.resolveRole(user);
                this.translate.use(this.translate.getDefaultLang());
                this.translate.use(user.language);
                localStorage.setItem('language', user.language);
                if (user.language === 'vi') {
                    document.body.classList.add('vietnum');
                } else {
                    document.body.classList.remove('vietnum');
                }
                if (user.language === 'ja') {
                    document.body.classList.add('japan');
                } else {
                    document.body.classList.remove('japan');
                }
                if (user.language === 'zh') {
                    document.body.classList.add('china');
                } else {
                    document.body.classList.remove('china');
                }
                // xamarinでandroidの実装をした人が、webviewでwindowにandroidという名前をつけたからiosでもこうせざるを得ない
                // yagni原則で必要最低限の機能を作るのは間違っていないが、
                // 目の前の必要にのみ応えるインターフェースを設計すると汚い設計になることが多いことの良い例
                // 必要最低限の機能をその機能に必要なもっともシンプルなインターフェースを設計すべき
                // @ts-ignore
                if (window.android) {
                    // @ts-ignore
                    window.android.onSuccessLogin(user.id.toString(),
                        REGISTER_DEVICE_TOKEN_URL,
                        MOBILE_APP_TOKEN_DELETION_URL_AFTER_LOGOUT);
                }
                this.mobileAppService.login(user.id);
                this.store.dispatch(new CurrentUserLoadedAction({user}));
                this.store.dispatch(new UpdateDirectChatUnreadIdsAction({unreadIds: user.directChatUnreadIds}));
            } else {
                this.store.dispatch(new LogoutAction());
            }
        })
    ), {dispatch: false});

    reloadCurrentUser$ = createEffect(() => this.actions$.pipe(
        ofType<ReloadCurrentUserAction>(AuthActionTypes.RELOAD_CURRENT_USER),
        mergeMap((action) => this.authService.getUserByToken(action.payload.token)),
        catchError((error, caught) => {
            this.store.dispatch(new LogoutAction());
            return caught;
        }),
        tap((user: UserModel) => {
            if (user) {
                this.authService.resolveRole(user);
                this.store.dispatch(new UpdateDirectChatUnreadIdsAction({unreadIds: user.directChatUnreadIds}));
                this.store.dispatch(new CurrentUserLoadedAction({user}));
            } else {
                this.store.dispatch(new LogoutAction());
            }
        })
    ), {dispatch: false});

    init$ = createEffect(() => defer(() => {
        const params   = parse(window.location.search.slice(1));
        const pathToken = params.token as string;
        const userToken = localStorage.getItem(environment.authTokenKey);
        let observableResult = of({type: 'NO_ACTION'});
        if (userToken) {
            observableResult = of(new LoginAction({authToken: userToken, pathToken}));
        }
        return observableResult;
    }));

    private returnUrl: string;

    constructor(private actions$: Actions,
                private router: Router,
                private authService: AuthService,
                private mobileAppService: MobileAppService,
                private store: Store<AppState>,
                private translate: TranslateService) {

        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.returnUrl = event.url;
            }
        });
    }
}
